// src/components/ui/data-table.jsx
//
// Project DataTable wrapper around TanStack Table.
//
// Purpose: a single table component that handles the patterns the codebase
// uses repeatedly — sticky first column, sticky header, clickable column-header
// sorting, scrollable container with rounded border, loading + empty states.
//
// Usage:
//   const columns = [
//     {
//       accessorKey: 'name',
//       header: 'Manager',
//       meta: { sticky: true },     // pin this column to the left
//     },
//     {
//       accessorKey: 'useful',
//       header: 'Useful Scorers',
//       meta: { numeric: true },    // right-align cell contents
//     },
//   ];
//   <DataTable columns={columns} data={rows} maxHeight={400} />
//
// Column meta options:
//   sticky:  boolean — pins the column to the left edge (use on first column)
//   numeric: boolean — right-aligns the cell text
//   cellClassName / headerClassName — extra Tailwind classes per cell/header
//
// The header cell is always sortable when `enableSorting` is true on the
// columnDef (which is the TanStack default). Pass `enableSorting: false` on
// a columnDef to opt out for that column.

import * as React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import PropTypes from 'prop-types';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { cn } from 'lib/utils';
import LoadingState from '../shared/LoadingState';

function SortIcon({ direction }) {
  // Active-only arrow (no idle chevron), matching the shared SortTh / Draft Room
  // sort affordance. Sortable columns still signal via cursor + hover.
  if (direction === 'asc') return <ArrowUp size={12} aria-hidden="true" />;
  if (direction === 'desc') return <ArrowDown size={12} aria-hidden="true" />;
  return null;
}

export function DataTable({
  columns,
  data,
  className,
  maxHeight,
  loading = false,
  loadingMessage = 'Loading…',
  emptyMessage = 'No data available.',
  initialSorting,
}) {
  const [sorting, setSorting] = React.useState(initialSorting || []);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (loading) {
    return <LoadingState message={loadingMessage} minHeight={Math.min(200, maxHeight || 200)} />;
  }

  if (!data?.length) {
    return (
      <div
        className={cn(
          'rounded-token-large border border-borderGrey bg-white p-6 text-center text-muted-foreground',
          className
        )}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'overflow-auto rounded-token-large border border-borderGrey bg-white',
        className
      )}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta || {};
                const isSticky = meta.sticky;
                const isNumeric = meta.numeric;
                const canSort = header.column.getCanSort();
                const sortDir = header.column.getIsSorted();

                return (
                  <th
                    key={header.id}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    aria-sort={
                      sortDir === 'asc'
                        ? 'ascending'
                        : sortDir === 'desc'
                          ? 'descending'
                          : canSort
                            ? 'none'
                            : undefined
                    }
                    className={cn(
                      'sticky top-0 bg-white border-b border-borderGrey font-semibold p-2 whitespace-nowrap',
                      isSticky ? 'left-0 z-[4]' : 'z-[3]',
                      isNumeric ? 'text-right' : 'text-left',
                      canSort && 'cursor-pointer hover:bg-neutral-50 select-none',
                      meta.headerClassName
                    )}
                  >
                    <div
                      className={cn(
                        'inline-flex items-center gap-1',
                        isNumeric && 'justify-end w-full'
                      )}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && <SortIcon direction={sortDir || null} />}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="group hover:bg-neutral-50">
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta || {};
                const isSticky = meta.sticky;
                const isNumeric = meta.numeric;

                return (
                  <td
                    key={cell.id}
                    className={cn(
                      'border-b border-borderGrey p-2 transition-colors',
                      // Sticky cell must mirror the row hover bg so the
                      // underlying scrolled content doesn't show through.
                      isSticky && 'sticky left-0 z-[1] bg-white group-hover:bg-neutral-50',
                      isNumeric && 'text-right',
                      meta.cellClassName
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  className: PropTypes.string,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
  loadingMessage: PropTypes.string,
  emptyMessage: PropTypes.string,
  initialSorting: PropTypes.array,
};

export default DataTable;
